import React, { useLayoutEffect, useRef } from 'react';

import { gql } from '@apollo/client';
import { EditableNameInput } from 'src/components';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { ReactEditor } from 'src/layout-engine';
import { useEditor } from 'src/layout-engine/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  input: {
    maxWidth: 400,
  },
}));

void gql`
  query GetDocumentName($documentId: UUID!) {
    document(documentId: $documentId) {
      __typename
      id
      name
    }
  }
`;

interface EditableDocumentNameProps {
  disabled?: boolean;
}

export function EditableDocName(props: EditableDocumentNameProps): JSX.Element {
  const editor = useEditor();
  const { classes } = useStyles();
  const {
    documentUi: { setRenameDoc },
  } = useEditorTabStateActions();
  const { documentId } = useDocumentParams();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [updateDocumentProps] = Apollo.useUpdateDocumentPropsMutation();
  const { data } = Apollo.useGetDocumentNameQuery({
    variables: { documentId },
  });
  const renameDoc = useEditorTabStateSelector(state => state.documentUi.renameDoc);

  useLayoutEffect(() => {
    if (renameDoc) inputRef?.current?.select();
  }, [renameDoc]);

  async function updateName(name: string): Promise<void> {
    await updateDocumentProps({
      variables: { input: { documentId, documentProps: { name } } },
    });

    setRenameDoc(false);
  }

  return (
    <EditableNameInput
      ref={inputRef}
      className={classes.input}
      name={data?.document?.name ?? ''}
      placeholder="Name your document..."
      updateName={updateName}
      onFocus={() => ReactEditor.blur(editor)}
      {...props}
    />
  );
}
